/** Lightweight, image-led boat and adventure picker; no second 3D renderer. */
import type { ModeKind } from './GameMode.ts'
import type { VesselKind } from '../entities/Tug.ts'

export interface GameSelection {
  mode: ModeKind
  vessel: VesselKind
}

const MODES: { kind: ModeKind; icon: string; title: string; blurb: string }[] = [
  { kind: 'open-water', icon: 'icon-waves', title: 'Open water', blurb: 'Just you and the big blue.' },
  { kind: 'icebreaker', icon: 'icon-snowflake', title: 'Icebreaker', blurb: 'Break a path with Sea Cruise.' },
  { kind: 'harbour', icon: 'icon-anchor', title: 'Harbour towage', blurb: 'A big barge. A little teamwork.' },
  { kind: 'beach', icon: 'icon-trees', title: 'Beach salvage', blurb: 'Bring the stranded logs home.' },
  { kind: 'landing', icon: 'icon-forklift', title: 'Beach landing', blurb: 'Ramp down. Forklift in.' },
]

/** Adventures built around one particular boat. */
const MODE_VESSEL: Partial<Record<ModeKind, VesselKind>> = { icebreaker: 'icebreaker', landing: 'landing-craft' }

const VESSELS: { kind: VesselKind; name: string; detail: string }[] = [
  { kind: 'tug', name: 'Atlantica', detail: 'The trusty tug' },
  { kind: 'icebreaker', name: 'Sea Cruise', detail: 'Made for the ice' },
  { kind: 'cruise-ship', name: 'Giant Charlie of the Sea', detail: 'A great big cruise ship' },
  { kind: 'container-ship', name: 'Ocean Atlas', detail: 'The huge container ship' },
  { kind: 'aircraft-carrier', name: 'Horizon', detail: 'Your very own aircraft carrier' },
  { kind: 'police-boat', name: 'Police patrol', detail: 'On sea patrol' },
  { kind: 'fireboat', name: 'Fireboat', detail: 'To the rescue' },
  { kind: 'sailboat', name: 'Sailing yacht', detail: 'Take the scenic route' },
  { kind: 'zodiac', name: 'Red Zodiac', detail: 'Small boat. Big zoom.' },
  { kind: 'landing-craft', name: 'Landing craft', detail: 'Drop the ramp on the beach' },
]

export function showTitleScreen(selectedVessel: VesselKind = 'tug', selectedMode: ModeKind = 'open-water'): Promise<GameSelection> {
  return new Promise((resolve) => {
    selectedVessel = MODE_VESSEL[selectedMode] ?? selectedVessel
    const overlay = document.createElement('div')
    overlay.className = 'title-screen'
    overlay.innerHTML = `
      <header class="title-header">
        <div class="game-wordmark"><span class="icon-ship" aria-hidden="true"></span>tug & barge<span class="brand-dot">.</span></div>
        <span class="title-tagline"><i aria-hidden="true"></i>A happy little adventure</span>
      </header>
      <form class="title-form">
        <main class="title-content">
          <div class="title-head">
            <div><p class="title-eyebrow">ALL ABOARD, CAPTAIN</p><h1>Make some <span>waves.</span></h1></div>
            <p>Pick your boat. <br>See where the sea takes you.</p>
          </div>
          <fieldset class="vessel-picker">
            <legend>Choose your boat</legend>
            <div class="vessel-grid">
              ${VESSELS.map(v => `
                <label class="vessel-option">
                  <input type="radio" name="vessel" value="${v.kind}" aria-label="${v.name}" ${selectedVessel === v.kind ? 'checked' : ''}>
                  <span class="vessel-check icon-check" aria-hidden="true"></span>
                  <img src="/images/vessels/${v.kind}.webp" alt="" width="680" height="400" draggable="false">
                  <span class="vessel-caption"><strong>${v.name}</strong><small>${v.detail}</small></span>
                </label>`).join('')}
            </div>
          </fieldset>
          <fieldset class="mode-picker">
            <legend>Choose your adventure</legend>
            <div class="title-cards">
              ${MODES.map(mode => `
                <label class="title-card">
                  <input type="radio" name="mode" value="${mode.kind}" aria-label="${mode.title}" ${selectedMode === mode.kind ? 'checked' : ''}>
                  <span class="title-card-icon ${mode.icon}" aria-hidden="true"></span>
                  <span><strong>${mode.title}</strong><small>${mode.blurb}</small></span>
                  <span class="mode-check icon-check" aria-hidden="true"></span>
                </label>`).join('')}
            </div>
          </fieldset>
        </main>
        <footer class="title-launch">
          <div class="title-launch-inner">
            <div class="title-selection" aria-live="polite" aria-atomic="true"><strong></strong><span></span></div>
            <button type="submit" class="sail-button"><span class="icon-play" aria-hidden="true"></span>Let’s go<span class="icon-arrow-right" aria-hidden="true"></span></button>
          </div>
        </footer>
      </form>`

    const selected = (name: 'vessel' | 'mode'): string => overlay.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`)!.value
    const choose = (name: 'vessel' | 'mode', value: string): void => { overlay.querySelector<HTMLInputElement>(`input[name="${name}"][value="${value}"]`)!.checked = true }
    const updateSelection = (): void => {
      overlay.querySelector('.title-selection strong')!.textContent = VESSELS.find(v => v.kind === selected('vessel'))!.name
      overlay.querySelector('.title-selection span')!.textContent = MODES.find(mode => mode.kind === selected('mode'))!.title
    }
    overlay.addEventListener('change', (event) => {
      const input = event.target as HTMLInputElement
      const needs = MODE_VESSEL[input.value as ModeKind]
      if (input.name === 'mode' && needs) choose('vessel', needs)
      // Picking a different boat keeps the choice honest: ice needs Sea Cruise,
      // and beach landings need the ramp.
      const required = MODE_VESSEL[selected('mode') as ModeKind]
      if (input.name === 'vessel' && required && input.value !== required) choose('mode', 'open-water')
      updateSelection()
    })
    let choosing = false
    overlay.querySelector('form')!.addEventListener('submit', (event) => {
      event.preventDefault()
      if (choosing) return
      choosing = true
      const mode = selected('mode') as ModeKind
      const vessel = selected('vessel') as VesselKind
      overlay.querySelector<HTMLButtonElement>('.sail-button')!.disabled = true
      overlay.classList.add('leaving')
      setTimeout(() => overlay.remove(), 220)
      resolve({ mode, vessel })
    })
    updateSelection()
    document.body.append(overlay)
  })
}
